import { ArrowLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateBeneficiary } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import { CpnPaymentReason } from '@/utils/cpn'
import { logger } from '@/utils/logger'

interface BankDetailsScreenProps {
  onNext: (beneficiary?: CpnBeneficiaryUser) => void
  onBack: () => void
  userId?: string // We'll need to pass this from parent
  existingBeneficiary?: CpnBeneficiaryUser // If editing existing beneficiary
  paymentReason: CpnPaymentReason // Payment reason code for the transfer
}

const BankDetailsScreen = ({
  onNext,
  onBack,
  userId,
  existingBeneficiary,
  paymentReason,
}: BankDetailsScreenProps) => {
  const [email, setEmail] = useState('')
  const [accountType, setAccountType] = useState('')
  const [transitNumber, setTransitNumber] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [saveForFuture, setSaveForFuture] = useState(true)
  const [institutionNumber, setInstitutionNumber] = useState('')
  const [city, setCity] = useState(existingBeneficiary?.city || '')
  const [street, setStreet] = useState(existingBeneficiary?.street || '')
  const [country, setCountry] = useState(existingBeneficiary?.country || '')
  const [lastName, setLastName] = useState(existingBeneficiary?.lastName || '')
  const [firstName, setFirstName] = useState(
    existingBeneficiary?.firstName || ''
  )
  const [postalCode, setPostalCode] = useState(
    existingBeneficiary?.postalCode || ''
  )
  const [stateProvince, setStateProvince] = useState(
    existingBeneficiary?.stateProvince || ''
  )

  // Hooks
  const createBeneficiaryMutation = useCreateBeneficiary()
  const { toast } = useToast()

  // Check if all required fields are filled
  const isFormValid =
    firstName &&
    lastName &&
    street &&
    city &&
    stateProvince &&
    postalCode &&
    country &&
    institutionNumber &&
    transitNumber &&
    accountNumber &&
    accountType

  const handleContinue = async () => {
    if (!isFormValid) return

    if (existingBeneficiary) {
      // If using existing beneficiary, just proceed
      onNext(existingBeneficiary)
      return
    }

    if (saveForFuture && userId) {
      // Create new beneficiary
      try {
        const result = await createBeneficiaryMutation.mutateAsync({
          userId,
          data: {
            firstName,
            lastName,
            street,
            city,
            stateProvince,
            postalCode,
            country,
            isBusiness: false,
            isSelf: existingBeneficiary?.isSelf ?? false,
            paymentReason: existingBeneficiary?.paymentReason ?? paymentReason,
          },
        })

        // Create a beneficiary object to pass to next screen
        const beneficiary: CpnBeneficiaryUser = {
          uuid: result.beneficiaryUuid,
          firstName,
          lastName,
          street,
          city,
          stateProvince,
          postalCode,
          country,
          isBusiness: false,
          isSelf: existingBeneficiary?.isSelf ?? false,
          paymentReason: existingBeneficiary?.paymentReason ?? paymentReason,
          metadata: {
            INSTITUTION_NUMBER: institutionNumber,
            TRANSIT_NUMBER: transitNumber,
            ACCOUNT_NUMBER: accountNumber,
            ACCOUNT_TYPE: accountType,
          },
        }

        toast({
          title: 'Recipient saved',
          description: `${firstName} ${lastName} has been saved for future transfers.`,
        })

        onNext(beneficiary)
      } catch (error) {
        logger.error('Failed to create beneficiary', error)
        toast({
          title: 'Error saving recipient',
          description: 'Failed to save recipient details. Please try again.',
          variant: 'destructive',
        })
        // Still proceed but don't save
        onNext()
      }
    } else {
      // Proceed without saving
      onNext()
    }
  }

  return (
    <div className="h-[600px] flex flex-col">
      <div className="p-4 space-y-1 flex-1 flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-1">
          <h1 className="text-lg font-bold text-gray-900">
            Enter their account details
          </h1>
        </div>

        {/* Form - Main content area - reduced spacing */}
        <div className="flex-1 space-y-1">
          {/* Email (Optional) */}
          <div className="space-y-1">
            <Label
              htmlFor="email"
              className="text-sm text-gray-700 font-medium"
            >
              Their email (optional)
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="example@example.ex"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* First Name */}
          <div className="space-y-1">
            <Label
              htmlFor="firstName"
              className="text-sm text-gray-700 font-medium"
            >
              First name
            </Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-1">
            <Label
              htmlFor="lastName"
              className="text-sm text-gray-700 font-medium"
            >
              Last name
            </Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Street Address */}
          <div className="space-y-1">
            <Label
              htmlFor="street"
              className="text-sm text-gray-700 font-medium"
            >
              Street address
            </Label>
            <Input
              id="street"
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* City */}
          <div className="space-y-1">
            <Label htmlFor="city" className="text-sm text-gray-700 font-medium">
              City
            </Label>
            <Input
              id="city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* State/Province */}
          <div className="space-y-1">
            <Label
              htmlFor="stateProvince"
              className="text-sm text-gray-700 font-medium"
            >
              State/Province
            </Label>
            <Input
              id="stateProvince"
              type="text"
              value={stateProvince}
              onChange={(e) => setStateProvince(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Postal Code */}
          <div className="space-y-1">
            <Label
              htmlFor="postalCode"
              className="text-sm text-gray-700 font-medium"
            >
              Postal code
            </Label>
            <Input
              id="postalCode"
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Institution Number */}
          <div className="space-y-1">
            <Label
              htmlFor="institution"
              className="text-sm text-gray-700 font-medium"
            >
              Institution number
            </Label>
            <Input
              id="institution"
              type="text"
              placeholder=""
              value={institutionNumber}
              onChange={(e) => setInstitutionNumber(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Transit Number */}
          <div className="space-y-1">
            <Label
              htmlFor="transit"
              className="text-sm text-gray-700 font-medium"
            >
              Transit number
            </Label>
            <Input
              id="transit"
              type="text"
              placeholder="020123456"
              value={transitNumber}
              onChange={(e) => setTransitNumber(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Account Number */}
          <div className="space-y-1">
            <Label
              htmlFor="account"
              className="text-sm text-gray-700 font-medium"
            >
              Account number
            </Label>
            <Input
              id="account"
              type="text"
              placeholder="12345678"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full h-8 text-sm"
            />
          </div>

          {/* Account Type */}
          <div className="space-y-1">
            <Label className="text-sm text-gray-700 font-medium">
              Account type
            </Label>
            <RadioGroup
              value={accountType}
              onValueChange={setAccountType}
              className="space-y-1"
            >
              <div className="flex items-center space-x-2 p-2 border border-gray-300 rounded-md">
                <RadioGroupItem
                  value="checking"
                  id="checking"
                  className="h-4 w-4"
                />
                <Label
                  htmlFor="checking"
                  className="flex-1 text-sm text-gray-900 cursor-pointer"
                >
                  Checking
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-2 border border-gray-300 rounded-md">
                <RadioGroupItem
                  value="savings"
                  id="savings"
                  className="h-4 w-4"
                />
                <Label
                  htmlFor="savings"
                  className="flex-1 text-sm text-gray-900 cursor-pointer"
                >
                  Savings
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Country */}
          <div className="space-y-1">
            <Label
              htmlFor="country"
              className="text-sm text-gray-700 font-medium"
            >
              Country
            </Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-full h-8 text-sm">
                <SelectValue placeholder="Choose a country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="us">United States</SelectItem>
                <SelectItem value="ca">Canada</SelectItem>
                <SelectItem value="uk">United Kingdom</SelectItem>
                <SelectItem value="de">Germany</SelectItem>
                <SelectItem value="fr">France</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Save for future transfers */}
          {!existingBeneficiary && (
            <div className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md bg-gray-50">
              <Checkbox
                id="saveForFuture"
                checked={saveForFuture}
                onCheckedChange={(checked) => setSaveForFuture(!!checked)}
              />
              <Label
                htmlFor="saveForFuture"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Save this recipient for future transfers
              </Label>
            </div>
          )}
        </div>

        {/* Buttons - reduced padding */}
        <div className="pt-1 border-t border-gray-100">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={createBeneficiaryMutation.isPending}
              className="flex-1 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-yellow-50 hover:border-yellow-200 transition-colors h-10 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={handleContinue}
              disabled={!isFormValid || createBeneficiaryMutation.isPending}
              className="flex-1 bg-black text-white hover:bg-gray-800 font-medium transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed h-10 text-sm flex items-center justify-center space-x-2"
            >
              {createBeneficiaryMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              <span>
                {createBeneficiaryMutation.isPending ? 'Saving...' : 'Continue'}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Powered by Paytrie - Swapped order, reduced padding */}
      <div className="text-center p-1 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Powered by Paytrie</p>
        <img
          src="/paytrie-logo.png"
          alt="Paytrie"
          className="h-5 w-auto mx-auto"
        />
      </div>
    </div>
  )
}

export default BankDetailsScreen
